import { useState, useRef, useCallback, useEffect } from "react"
import { OriginRelaySymbol } from "@/components/icons/OriginRelaySymbol"
import { Input } from "@/components/ui/input"
import { StepFormLayout, ContinueButton } from "./primitives"
import { cn } from "@/lib/utils"
import { isIPad } from "@/lib/platform"

interface EmailVerificationStepProps {
  onVerified: () => void
  /** Previously verified email, pre-filled when the user must re-verify */
  initialEmail?: string
}

export function EmailVerificationStep({ onVerified, initialEmail }: EmailVerificationStepProps) {
  const [phase, setPhase] = useState<'email' | 'otp'>('email')
  const [email, setEmail] = useState(initialEmail ?? '')
  const [otpDigits, setOtpDigits] = useState<string[]>(Array(8).fill(''))
  const [error, setError] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [resendCooldown, setResendCooldown] = useState(0)

  const otpRefs = useRef<(HTMLInputElement | null)[]>([])

  // Resend cooldown timer
  useEffect(() => {
    if (resendCooldown <= 0) return
    const timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000)
    return () => clearTimeout(timer)
  }, [resendCooldown])

  const handleSendOtp = useCallback(async () => {
    const normalizedEmail = email.trim().toLowerCase()

    if (!normalizedEmail) {
      setError('Please enter your email address.')
      return
    }

    const domain = normalizedEmail.split('@')[1]
    if (domain !== 'useorigin.com') {
      setError('Only @useorigin.com email addresses are allowed.')
      return
    }

    setError(null)
    setIsLoading(true)
    try {
      const result = await window.electronAPI.sendOtp(normalizedEmail)
      if (result.success) {
        setPhase('otp')
        setResendCooldown(60)
        setOtpDigits(Array(8).fill(''))
        // Focus first OTP input after transition
        setTimeout(() => otpRefs.current[0]?.focus(), 100)
      } else {
        setError(result.error || 'Failed to send verification code.')
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Unknown error'
      setError(`Failed to send verification code: ${msg}`)
    } finally {
      setIsLoading(false)
    }
  }, [email])

  const handleVerifyOtp = useCallback(async (code: string) => {
    const normalizedEmail = email.trim().toLowerCase()
    setError(null)
    setIsLoading(true)
    try {
      const result = await window.electronAPI.verifyOtp(normalizedEmail, code)
      if (result.success) {
        onVerified()
      } else {
        setError(result.error || 'Invalid verification code.')
        setOtpDigits(Array(8).fill(''))
        setTimeout(() => otpRefs.current[0]?.focus(), 100)
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Unknown error'
      setError(`Verification failed: ${msg}`)
      setOtpDigits(Array(8).fill(''))
      setTimeout(() => otpRefs.current[0]?.focus(), 100)
    } finally {
      setIsLoading(false)
    }
  }, [email, onVerified])

  const handleOtpChange = useCallback((index: number, value: string) => {
    // Only allow digits
    const digit = value.replace(/\D/g, '').slice(-1)
    setOtpDigits(prev => {
      const next = [...prev]
      next[index] = digit
      // Auto-submit when all 6 digits are filled
      if (digit && next.every(d => d !== '')) {
        handleVerifyOtp(next.join(''))
      }
      return next
    })
    // Auto-focus next input
    if (digit && index < 7) {
      otpRefs.current[index + 1]?.focus()
    }
  }, [handleVerifyOtp])

  const handleOtpKeyDown = useCallback((index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !otpDigits[index] && index > 0) {
      otpRefs.current[index - 1]?.focus()
    }
  }, [otpDigits])

  const handleOtpPaste = useCallback((e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault()
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 8)
    if (!pasted) return
    const digits = pasted.split('')
    setOtpDigits(prev => {
      const next = [...prev]
      digits.forEach((d, i) => { next[i] = d })
      if (next.every(d => d !== '') && next.length === 8) {
        handleVerifyOtp(next.join(''))
      }
      return next
    })
    // Focus the input after the last pasted digit
    const focusIndex = Math.min(digits.length, 7)
    otpRefs.current[focusIndex]?.focus()
  }, [handleVerifyOtp])

  const handleResend = useCallback(async () => {
    if (resendCooldown > 0 || isLoading) return
    setError(null)
    setIsLoading(true)
    try {
      const normalizedEmail = email.trim().toLowerCase()
      const result = await window.electronAPI.sendOtp(normalizedEmail)
      if (result.success) {
        setResendCooldown(60)
        setOtpDigits(Array(8).fill(''))
        setTimeout(() => otpRefs.current[0]?.focus(), 100)
      } else {
        setError(result.error || 'Failed to resend code.')
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Unknown error'
      setError(`Failed to resend code: ${msg}`)
    } finally {
      setIsLoading(false)
    }
  }, [email, resendCooldown, isLoading])

  if (phase === 'email') {
    return (
      <StepFormLayout
        iconElement={
          <div className="flex size-16 items-center justify-center">
            <OriginRelaySymbol className="size-10 text-foreground" />
          </div>
        }
        title="Verify your identity"
        description="Enter your Origin email to get started."
        actions={
          <ContinueButton
            onClick={handleSendOtp}
            className="w-full"
            loading={isLoading}
            loadingText="Sending code..."
            disabled={!email.trim()}
          >
            Continue
          </ContinueButton>
        }
      >
        <div className="space-y-2">
          <Input
            type="email"
            placeholder="you@useorigin.com"
            value={email}
            onChange={e => { setEmail(e.target.value); setError(null) }}
            onKeyDown={e => { if (e.key === 'Enter') handleSendOtp() }}
            autoFocus={!isIPad}
            className="w-full"
          />
          {error && (
            <p className="text-sm text-destructive">{error}</p>
          )}
        </div>
      </StepFormLayout>
    )
  }

  // OTP phase
  return (
    <StepFormLayout
      iconElement={
        <div className="flex size-16 items-center justify-center">
          <OriginRelaySymbol className="size-10 text-foreground" />
        </div>
      }
      title="Check your email"
      description={<>We sent a code to <strong>{email.trim().toLowerCase()}</strong></>}
    >
      <div className="space-y-4">
        <div className="flex justify-center gap-1.5">
          {otpDigits.map((digit, i) => (
            <input
              key={i}
              ref={el => { otpRefs.current[i] = el }}
              type="text"
              inputMode="numeric"
              pattern="[0-9]"
              maxLength={1}
              value={digit}
              onChange={e => handleOtpChange(i, e.target.value)}
              onKeyDown={e => handleOtpKeyDown(i, e)}
              onPaste={i === 0 ? handleOtpPaste : undefined}
              disabled={isLoading}
              className={cn(
                "w-10 h-12 text-center text-lg font-mono rounded-lg border border-border bg-foreground-2",
                "focus:outline-none focus:ring-2 focus:ring-accent focus:border-accent",
                "disabled:opacity-50",
                "text-foreground"
              )}
              autoFocus={i === 0 && !isIPad}
            />
          ))}
        </div>

        {error && (
          <p className="text-sm text-destructive text-center">{error}</p>
        )}

        <div className="flex flex-col items-center gap-2 text-sm">
          <button
            onClick={handleResend}
            disabled={resendCooldown > 0 || isLoading}
            className={cn(
              "text-muted-foreground hover:text-foreground transition-colors",
              (resendCooldown > 0 || isLoading) && "opacity-50 cursor-not-allowed"
            )}
          >
            {resendCooldown > 0 ? `Resend code in ${resendCooldown}s` : 'Resend code'}
          </button>
          <button
            onClick={() => { setPhase('email'); setError(null); setOtpDigits(Array(8).fill('')) }}
            disabled={isLoading}
            className="text-muted-foreground hover:text-foreground transition-colors"
          >
            Use a different email
          </button>
        </div>
      </div>
    </StepFormLayout>
  )
}
